import { FC, PropsWithChildren, ReactNode, Ref } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { WeaveUpgradeIcon } from '@frontend/self-serve#svg';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { NakedButton, Text } from '@frontend/design-system';
import { useEIVWidgetStateStore } from './eiv-widget-state.store';

/**
 * Optional visual overrides for the EIV widget chrome. Callers (e.g. the
 * Eligibility List setup banner) can pass these to reuse the same widget
 * with a different title/subtitle and a flatter, transparent look without
 * forking the component. When omitted, the dashboard widget renders
 * identically to before.
 */
export interface WidgetAppearance {
  title?: string;
  subtitle?: string;
  hideUpgradeIcon?: boolean;
  surfaceStyles?: Interpolation<Theme>;
}

export const WidgetLayout: FC<
  PropsWithChildren<{
    containerStyles?: Interpolation<Theme>;
    isAccordion?: boolean;
    headerActions?: ReactNode;
    innerRef?: Ref<HTMLDivElement>;
    noBorder?: boolean;
    isFreeTrialWidget?: boolean;
    isTrialActive?: boolean;
    eligibilityBannerAppearance?: WidgetAppearance;
  }>
> = ({
  containerStyles,
  isAccordion,
  children,
  headerActions,
  innerRef,
  noBorder,
  isTrialActive,
  isFreeTrialWidget = true,
  eligibilityBannerAppearance,
}) => {
  const { isCollapsed } = useEIVWidgetStateStore();
  return (
    <div css={[defaultStyles.container, noBorder && defaultStyles.noBorder, containerStyles]} ref={innerRef}>
      <WidgetHeader
        showExpandCollapse={isAccordion}
        headerActions={headerActions}
        isFreeTrialWidget={isFreeTrialWidget}
        eligibilityBannerAppearance={eligibilityBannerAppearance}
      />
      {!(isAccordion && isCollapsed) && !!children && (
        <div css={[defaultStyles.body, eligibilityBannerAppearance?.surfaceStyles]}>{children}</div>
      )}
      {isTrialActive && <WidgetFooter />}
    </div>
  );
};

const WidgetHeader: FC<{
  showExpandCollapse?: boolean;
  headerActions?: ReactNode;
  isFreeTrialWidget?: boolean;
  eligibilityBannerAppearance?: WidgetAppearance;
}> = ({ showExpandCollapse, headerActions, isFreeTrialWidget = true, eligibilityBannerAppearance }) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });
  const { isCollapsed, toggleExpandCollapse } = useEIVWidgetStateStore();
  const { title, subtitle, hideUpgradeIcon, surfaceStyles } = eligibilityBannerAppearance ?? {};
  const headerText =
    title ?? (isFreeTrialWidget ? t('Free Trial of Weave Insurance Eligibility') : t('Weave Insurance Eligibility'));
  const { mutate: toggleWidgetState, isLoading: isActionInProgress } =
    PlatformLocationFeatureV1.Mutations.useCreateOrUpdateFeatureNotificationUserActionMutation({
      options: {
        onError: (error: HttpError) => {
          sentry.warn({
            error: `Error toggling EIV widget state from dashboard: ${error?.message}`,
            topic: 'customer-lifecycle-management',
            team: 'fe-clm',
            addContext: {
              name: 'error',
              context: { error },
            },
          });
        },
      },
    });

  const handleToggleWidgetState = () => {
    toggleWidgetState({
      actionEnum: isCollapsed
        ? PlatformLocationFeatureV1.Types.Action.ACTION_EXPAND
        : PlatformLocationFeatureV1.Types.Action.ACTION_COLLAPSE,
      notificationFeature: PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_EIV,
      sourceEnum: [PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_TEASER_BANNER],
    });
    toggleExpandCollapse();
  };

  return (
    <div css={[defaultStyles.header, surfaceStyles]}>
      <div css={defaultStyles.headerTitle}>
        {!hideUpgradeIcon && <WeaveUpgradeIcon css={defaultStyles.upgradeIcon} customFallback={<></>} />}
        <div css={defaultStyles.headerTitleText}>
          <Text weight='bold'>{headerText}</Text>
          {subtitle && (
            <Text size='small' color='light'>
              {subtitle}
            </Text>
          )}
        </div>
      </div>
      {!!headerActions && <div css={defaultStyles.headerActions}>{headerActions}</div>}
      {showExpandCollapse && (
        <NakedButton
          onClick={handleToggleWidgetState}
          aria-label={isCollapsed ? t('Expand widget') : t('Collapse widget')}
          disabled={isActionInProgress}
        >
          <Icon
            name='caret-up-small'
            css={[defaultStyles.collapseToggleBtn, isCollapsed && defaultStyles.collapseIcon]}
          />
        </NakedButton>
      )}
    </div>
  );
};

const WidgetFooter = () => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });
  return (
    <div css={defaultStyles.footer}>
      <Icon name='error-filled' />
      <Text size='small' weight='bold' css={{ color: theme.colors.critical70 }}>
        {t(
          'Payer credentials are missing. Insurance eligibility results may be incomplete. Connect payers to ensure full verification.'
        )}
      </Text>
    </div>
  );
};
const defaultStyles = {
  container: css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.large};
    border: 1px solid ${theme.colors.secondary.seaweed50};
    box-shadow: ${theme.shadows.floating};
    position: relative;
    overflow: hidden;
    container-type: inline-size;
  `,
  header: css`
    display: grid;
    background-color: ${theme.colors.secondary.seaweed50}1a;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(1)};
    grid-template-areas: 'title actions collapse';
    grid-template-columns: 1fr auto auto;
    grid-template-rows: auto;
    align-items: center;
    @container (max-width: ${breakpoints.small.max}px) {
      grid-template-columns: 1fr auto;
      grid-template-areas:
        'title collapse'
        'actions actions';
    }
  `,
  footer: css`
    background-color: ${theme.colors.critical10};
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing(1)};
  `,
  upgradeIcon: css`
    width: 24px;
    height: 24px;
    scale: 1.35; // adjust scale as image has extra padding
    position: relative;
    top: 2px;
  `,
  headerTitle: css`
    grid-area: title;
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  headerTitleText: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.25)};
  `,
  headerActions: css`
    grid-area: actions;
  `,
  collapseToggleBtn: css`
    grid-area: collapse;
    transition: transform 0.3s ease-in-out;
  `,
  collapseIcon: css`
    transform: rotate(180deg);
  `,
  body: css`
    padding: ${theme.spacing(2)};
    background-color: ${theme.colors.white};
  `,
  noBorder: css`
    border: none;
  `,
};
